import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useIsMobile } from 'hooks/useIsMobile';
import { RoutesNames } from '../../RoutesNames';
import type { TAppBarHandle } from './index';

type TAppBarLoggedOutProps = {
  handleMenuClick: () => void;
};

const AppBarLoggedOut = forwardRef<TAppBarHandle, TAppBarLoggedOutProps>(({ handleMenuClick }, ref) => {
  const isMobile = useIsMobile(768);

  // Forwarded handle is unused when logged out, but kept for parent compatibility.
  React.useImperativeHandle(ref, () => ({
    logout: async () => {
      /* no-op: no session to sign out when logged out */
    },
  }));

  return (
    <SHeader>
      <SToolbar>
        {isMobile && (
          <SIconButton onClick={handleMenuClick} aria-label='menu'>
            <SMenuSvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
              <path d='M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z' />
            </SMenuSvg>
          </SIconButton>
        )}
        <SButtonsWrapper>
          <SIconsContainer>
            <SIconButton aria-label='sign in'>
              <Link to={RoutesNames.SignIn}>
                <SAccountSvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                  <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z' />
                </SAccountSvg>
              </Link>
            </SIconButton>
          </SIconsContainer>
        </SButtonsWrapper>
      </SToolbar>
    </SHeader>
  );
});

AppBarLoggedOut.displayName = 'AppBarLoggedOut';

export default AppBarLoggedOut;

const SHeader = styled.header`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 100;
  background: #fcc425;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);

  @media (max-width: 1024px) {
    height: 72px;
    display: flex;
    justify-content: center;
  }
`;

const SToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 16px;
  min-height: 64px;
  width: 100%;

  @media (max-width: 1024px) {
    justify-content: space-between;
  }
`;

const SButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const SIconsContainer = styled.div`
  display: flex;
`;

const SIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;
`;

const SMenuSvg = styled.svg`
  width: 24px;
  height: 24px;
  fill: #0d0d0d;
`;

const SAccountSvg = styled.svg`
  width: 24px;
  height: 24px;
  fill: #0d0d0d;
  display: flex;

  @media (max-width: 1024px) {
    margin-left: 20px;
  }
`;
